import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';

import { Text, TextType } from 'ui-components/components/text';

import { AddPackageSetButton } from './add-package-set-button';
import { messages } from './messages';
import { PackageSetsTable } from './package-sets-table/package-sets-table';
import style from './style.module.less';
import { setupPackageSet } from '../../../../components/modals/multi-package-modal/setup-package-set';
import { emptyArray } from 'ui-components/default-props/empty';
import Global from '../../../../store/global-selectors';
import { useEnableSupplyPage } from '../../../../feature-flags/hooks/use-enable-supply-page';
import { SuppliesCardContainer } from '../../../home-page/version2/supplies-cards/supplies-card-container';
import { SUPPLIES_DISPLAY_LOCATION } from '../../../home-page/version2/supplies-cards/supplies-page-constants';

/**
 * @typedef {import('../../../../components/modals/multi-package-modal/package-set').PackageSetWithJsonData} PackageSetWithJsonData
 * @typedef {import('../../../../components/modals/multi-package-modal/package-set').SetupPackageSet} SetupPackageSet
 * @typedef {import('../../../../prop-types/dimenstion-unit').DimensionUnit} DimensionUnit
 */

const getBodyRowClassname = (isLastInSet, isLastSet) => {
  if (!isLastInSet) return style['package-row'];
  return isLastSet ? style['last-package-set-row'] : style['package-set-row'];
};

/**
 * Parses a package set and returns a new object with the package set ID, name, and packages
 * @param {PackageSetWithJsonData} packageSet - The package set to parse
 * @param {DimensionUnit} sellerDefaultDimensionsUnit - The seller's default dimensions unit
 * @returns {SetupPackageSet} The parsed package set
 */
const parsePackageSet = (packageSet, sellerDefaultDimensionsUnit) => {
  const { packageSetId, name } = packageSet;
  const packages = setupPackageSet(packageSet, sellerDefaultDimensionsUnit);

  return {
    packageSetId,
    packageSetName: name,
    packages: packages || emptyArray,
  };
};

export const PackageSets = () => {
  const enableSupplyPage = useEnableSupplyPage();
  const packageSets = useSelector(Global.selectSellerPackageSets);
  const sellerDefaultDimensionsUnit = useSelector(Global.selectSellerDefaultDimensionUnit);
  const sellerDefaultWeightUnit = useSelector(Global.selectSellerDefaultWeightUnit);

  const parsedPackageSets = useMemo(() => {
    return packageSets?.length ? packageSets.map((packageSet) => parsePackageSet(packageSet, sellerDefaultDimensionsUnit)) : emptyArray;
  }, [packageSets, sellerDefaultDimensionsUnit]);

  const tableData = useMemo(() => {
    const nonEmptyPackageSets = parsedPackageSets.filter((set) => set.packages.length > 0);
    const lastIndex = nonEmptyPackageSets.length - 1;

    return nonEmptyPackageSets.flatMap((set, setIndex) => {
      const isLastSet = setIndex === lastIndex;
      return set.packages.map((pkg, pkgIndex) => {
        const isLastInSet = pkgIndex === set.packages.length - 1;
        const bodyRowClassName = getBodyRowClassname(isLastInSet, isLastSet);
        return {
          ...pkg,
          packageSetId: set.packageSetId,
          packageSetName: set.packageSetName,
          isFirstInSet: pkgIndex === 0,
          isLastInSet,
          isLastSet,
          bodyRowClassName,
        };
      });
    });
  }, [parsedPackageSets]);

  return (
    <>
      <Text className={style['package-sets-description']} textType={TextType.Description} value={messages.packageSetsDescription} />
      <AddPackageSetButton />
      <PackageSetsTable
        data={tableData}
        sellerDefaultDimensionsUnit={sellerDefaultDimensionsUnit}
        sellerDefaultWeightUnit={sellerDefaultWeightUnit}
      />
      {enableSupplyPage && <SuppliesCardContainer page={SUPPLIES_DISPLAY_LOCATION.SETTINGS_PACKAGES} />}
    </>
  );
};
